import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, NavLink, useNavigate } from 'react-router-dom';
import LandingPage from './components/LandingPage';
import LogicConversion from './components/LogicConversion';
import SqlOptimization from './components/SqlOptimization';
import UploadJobs from './components/UploadJobs';
import Validation from './components/Validation';
import Configuration from './components/Configuration';
import VideosLibrary from './components/VideosLibrary';

const AUTH_STORAGE_KEY = 'scada_auth_session';

const getMidlayerServiceUrl = () => {
  return process.env.REACT_APP_UPSTREAMAI_MIDLAYER_SERVICE_URL || 'http://localhost:20021/api';
};

const getStoredConfig = () => {
  const savedConfig = localStorage.getItem('scada_api_config');
  const defaultBase = process.env.REACT_APP_API_BASE || 'http://localhost:20021/api';

  if (!savedConfig) {
    return { apiUrl: defaultBase };
  }

  try {
    const parsed = JSON.parse(savedConfig);
    return {
      apiUrl: parsed.apiUrl || parsed.apiBase || parsed.tag_conversion || defaultBase,
    };
  } catch (_error) {
    return { apiUrl: defaultBase };
  }
};

const buildAuthUrl = () => {
  const loginBase = getMidlayerServiceUrl();
  const normalized = String(loginBase || '').replace(/\/+$/, '');
  if (normalized.endsWith('/api/v1')) {
    return `${normalized}/auth/login`;
  }
  if (normalized.endsWith('/api')) {
    return `${normalized}/v1/auth/login`;
  }
  return `${normalized}/api/v1/auth/login`;
};

function LoginPanel({ onLoginSuccess }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const loginUrl = buildAuthUrl();
      const response = await fetch(loginUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        const message = payload?.error?.message || payload?.message || 'Login failed. Please verify your credentials.';
        throw new Error(message);
      }

      if (!payload?.data?.accessToken) {
        throw new Error('Login response did not include an access token.');
      }

      onLoginSuccess(payload.data);
      navigate('/');
    } catch (requestError) {
      setError(requestError.message || 'Unable to login right now.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-shell">
      <div className="auth-card">
        <h2 className="page-title" style={{ marginBottom: '0.75rem' }}>Sign In</h2>
        <p className="page-subtitle" style={{ marginBottom: '1.5rem' }}>
          Use your Upstream AI credentials to continue.
        </p>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Username or Email</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Enter username"
              autoComplete="username"
              required
            />
          </div>
          <div className="form-group">
            <label>Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter password"
              autoComplete="current-password"
              required
            />
          </div>
          {error && <div className="alert alert-error" style={{ marginBottom: '1rem' }}>{error}</div>}
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
      </div>
    </div>
  );
}

function App() {
  const [logo, setLogo] = useState('/banner.png');
  const [auth, setAuth] = useState(() => {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    try {
      return JSON.parse(raw);
    } catch (_error) {
      return null;
    }
  });

  const updateLogo = () => {
    const savedConfig = localStorage.getItem('scada_api_config');
    if (savedConfig) {
      const parsed = JSON.parse(savedConfig);
      setLogo(parsed.company_logo || '/banner.png');
    }
  };

  useEffect(() => {
    updateLogo();
    window.addEventListener('configUpdated', updateLogo);
    return () => window.removeEventListener('configUpdated', updateLogo);
  }, []);

  const handleLoginSuccess = (sessionData) => {
    setAuth(sessionData);
    localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(sessionData));
  };

  const handleLogout = () => {
    setAuth(null);
    localStorage.removeItem(AUTH_STORAGE_KEY);
  };

  return (
    <Router>
      {!auth?.accessToken ? (
        <LoginPanel onLoginSuccess={handleLoginSuccess} />
      ) : (
        <div className="app">
          <nav className="navbar">
            <div className="navbar-brand-wrap">
              <div className="navbar-brand">
                <img src={logo} alt="Company Logo" style={{ height: '50px', maxWidth: '180px', objectFit: 'contain' }} />
                <div style={{ width: '1px', height: '28px', backgroundColor: 'var(--border)' }}></div>
                <h1 style={{ background: 'linear-gradient(135deg, #6b5fa0 0%, #5145a8 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', fontWeight: '700', fontSize: '1.1rem', margin: 0, lineHeight: 1.1 }}>
                  Wipro<br />UpstreamAI
                </h1>
              </div>
            </div>
            <div className="navbar-actions">
              <div className="nav-links">
                <NavLink to="/" className={({ isActive }) => isActive ? 'active' : ''}>
                  Home
                </NavLink>
                <NavLink to="/upload" className={({ isActive }) => isActive ? 'active' : ''}>
                  Tag Conversion
                </NavLink>
                <NavLink to="/logic" className={({ isActive }) => isActive ? 'active' : ''}>
                  Quick Script Conversion
                </NavLink>
                <NavLink to="/sql" className={({ isActive }) => isActive ? 'active' : ''}>
                  SQL Optimization
                </NavLink>
                <NavLink to="/validation" className={({ isActive }) => isActive ? 'active' : ''}>
                  Validation
                </NavLink>
                <NavLink to="/videos" className={({ isActive }) => isActive ? 'active' : ''}>
                  Videos
                </NavLink>
                <NavLink to="/config" className={({ isActive }) => isActive ? 'active' : ''} title="Settings">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle' }}>
                    <circle cx="12" cy="12" r="3"></circle>
                    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
                  </svg>
                </NavLink>
              </div>
              <div className="navbar-user-actions">
                <span className="navbar-username">
                  {auth?.user?.username || auth?.user?.email || 'Authenticated User'}
                </span>
                <button type="button" className="btn btn-secondary btn-sm" onClick={handleLogout}>
                  Logout
                </button>
              </div>
            </div>
          </nav>
          <main className="main-content">
            <Routes>
              <Route path="/" element={<LandingPage auth={auth} />} />
              <Route path="/logic" element={<LogicConversion />} />
              <Route path="/sql" element={<SqlOptimization />} />
              <Route path="/upload" element={<UploadJobs />} />
              <Route path="/validation" element={<Validation />} />
              <Route path="/videos" element={<VideosLibrary />} />
              <Route path="/config" element={<Configuration auth={auth} onAuthExpired={handleLogout} midlayerServiceUrl={getMidlayerServiceUrl()} />} />
            </Routes>
          </main>
        </div>
      )}
    </Router>
  );
}

export default App;
